import { bs58 } from '@project-serum/anchor/dist/cjs/utils/bytes';
import { Connection, LAMPORTS_PER_SOL, PublicKey, Transaction } from '@solana/web3.js';
import BigNumber from 'bignumber.js';
import dayjs from 'dayjs';
import md5 from 'md5';
import numbro from 'numbro';
import { v4 as uuidv4 } from 'uuid';
import CommonApi from '../apis/common-api';
import connection from '../apis/connection';
import database from '../apis/data/database';
import { icon as solanaIcon, lightIcon as solanaLightIcon } from '../apis/data/icon/solana';
import { defaultTokenIcon, getChainInfo } from '../apis/data/index';
import SolanaRpc from '../apis/rpc/solana-rpc';
import { DateFormat, STATIC_PARTICLE_NETWORK, SolanaNetworkFee, WSOL_TOKEN_ADDRESS } from '../config';
import { formatAmount, getCacheTokenList, toNonExponential } from '../utils/index';
export class SolanaService {
    wallet;
    provider;
    particleProvider;
    projectId;
    clientKey;
    commonApi;
    rpc;
    rpcUrl;
    chainName;
    chainId;
    chainType;
    currency = 'USD';
    appId;
    t;
    mockAddress;
    database;
    smartAccount = {};
    constructor(provider, particleProvider, options) {
        this.chainType = 'solana';
        this.provider = provider;
        this.particleProvider = particleProvider;
        this.appId = options.appId;
        this.chainId = options.chainId;
        this.chainName = options.chainName.toLowerCase();
        this.projectId = options.projectId;
        this.clientKey = options.clientKey;
        this.mockAddress = options.mockAddress;
        const rpcUrl = this.provider?.rpcUrl || `${process.env.NEXT_PUBLIC_RPC_URL}/solana${process.env.NEXT_PUBLIC_USE_PUBLIC_RPC === 'true' ? '/public' : ''}`;
        this.rpcUrl = rpcUrl + `?chainId=${this.chainId}&projectUuid=${this.projectId}&projectKey=${this.clientKey}`;
        console.log(`rpcUrl: ${this.rpcUrl}`);
        this.rpc = new SolanaRpc(connection(this.rpcUrl, this.projectId, this.clientKey));
        this.commonApi = new CommonApi(connection(STATIC_PARTICLE_NETWORK, this.projectId, this.clientKey));
        this.currency = options.currency || 'USD';
        this.t = options.t;
        this.database = database;
    }
    accountAbstractionChains = (aaName) => [];
    enableAccountAbstraction = () => {
        return false;
    };
    async getAddressFromDID(did) {
        throw new Error('Current chain does not support DID');
    }
    async getDIDFromAddress(address) {
        throw new Error('Current chain does not support DID');
    }
    async request(options = {}) {
        const { id, primaryKey, fn, dataBaseModel, useCache = true, isRequest = true, throttleWait = 0 } = options || {};
        let data;
        if (primaryKey) {
            data = await dataBaseModel.get(primaryKey).catch(database.errorHandler);
        }
        else {
            data = await dataBaseModel.get({ id }).catch(database.errorHandler);
        }
        if (isRequest === true) {
            const isUseCache = useCache && data && data.updateTime && dayjs().diff(dayjs(data.updateTime), 'day') < 1;
            if (!isUseCache) {
                let res = await fn(data);
                if (Array.isArray(res)) {
                    res = { list: res };
                }
                else if (typeof res !== 'object') {
                    res = { value: res };
                }
                data = {
                    id,
                    ...(primaryKey || {}),
                    ...res,
                    updateTime: dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss'),
                };
                try {
                    await dataBaseModel.put(data);
                }
                catch (error) {
                    // ignore
                }
            }
            else {
                if (throttleWait === 0 || !data?.updateTime || (throttleWait > 0 && data?.updateTime && new Date().getTime() - new Date(data.updateTime).getTime() > throttleWait)) {
                    fn(data)
                        .then((res) => {
                        if (Array.isArray(res)) {
                            res = { list: res };
                        }
                        else if (typeof res !== 'object') {
                            res = { value: res };
                        }
                        dataBaseModel.put({
                            id,
                            ...(primaryKey || {}),
                            ...res,
                            updateTime: dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss'),
                        });
                    })
                        .catch((err) => {
                        console.log(dataBaseModel.name, err);
                    });
                }
            }
        }
        return data;
    }
    setCurrency(currency) {
        this.currency = currency;
    }
    getConnection() {
        if (this.provider.getConnection) {
            return this.provider.getConnection();
        }
        else if (this.particleProvider.getConnection) {
            return this.particleProvider.getConnection();
        }
        else {
            return new Connection(this.rpcUrl, 'confirmed');
        }
    }
    async getBalance() {
        const address = await this.getAddress();
        if (!address) {
            return 0;
        }
        const publicKey = new PublicKey(address);
        if (publicKey) {
            const id = md5(`${this.chainName}_${this.chainId}_${address}`);
            let { value: balance } = await this.request({
                id,
                fn: () => {
                    return this.getConnection().getBalance(publicKey);
                },
                dataBaseModel: database.balanceInfo,
            });
            return Number(balance || 0);
        }
        else {
            return 0;
        }
    }
    async getNftMetadata(uri, useCache = true) {
        const id = md5(uri);
        return this.request({
            id,
            useCache,
            fn: async () => {
                return this.rpc.getNftMetadata(uri).then((res) => {
                    let { description, image, name, seller_fee_basis_points, symbol } = res || {};
                    return {
                        description,
                        image,
                        name,
                        seller_fee_basis_points,
                        symbol,
                    };
                });
            },
            dataBaseModel: database.nftMetadata,
        });
    }
    async getUserSimpleInfo(params) {
        return this.commonApi.getUserSimpleInfo(params);
    }
    async getRequestTokenAddress() {
        let addressList = [];
        // 2：importTokenList
        // @ts-ignore
        const userAddress = window.__webWalletData.address.toLowerCase();
        // 1: read storage tokenList
        const totkenList = getCacheTokenList(userAddress, this.chainName, Number(this.chainId));
        addressList = [...totkenList.map((item) => item.address), 'native'];
        // @ts-ignore
        const chainNetwork = window.__webWalletData.chainInfo.chainNetwork.toLowerCase();
        const impoertTokenList = await this.getImpoertTokenList({ userAddress, chainNetwork });
        addressList.push(...impoertTokenList.map((item) => item.tokenAddress));
        // @ts-ignore
        if (Number(window.__appParams.paramsChainId) === this.chainId) {
            // 3: displayTokenAddresses
            // @ts-ignore
            addressList.push(...(window.__appParams.displayTokenAddresses || []));
            // 4: priorityTokenAddresses
            // @ts-ignore
            addressList.push(...(window.__appParams.priorityTokenAddresses || []));
        }
        // 5:filter hiddenAddressList
        const hiddenAddressList = impoertTokenList.filter((item) => item.isHide).map((item) => item.address);
        addressList = addressList.filter((address) => address && address.length > 5 && !hiddenAddressList.includes(address));
        // 6: filter repeat
        addressList = Array.from(new Set(addressList.map((item) => item.toLowerCase())));
        return addressList;
    }
    async getTokenAndNftCacheKey() {
        const walletAccount = await this.getAddress();
        return md5(`${this.chainName}_${this.chainId}_${walletAccount}`.toLowerCase());
    }
    async getTokensAndNFTs(chainName, chainId) {
        chainName = chainName || this.chainName;
        chainId = chainId || Number(this.chainId);
        const walletAddress = await this.getAddress();
        const id = await this.getTokenAndNftCacheKey();
        let balance = await this.getBalance();
        let tokenData = await this.getAllToken();
        const importTokenList = await this.getImpoertTokenList({ userAddress: walletAddress.toLowerCase() });
        const tokenAddressList = await this.getRequestTokenAddress();
        let tokensAndNFTs = await this.rpc?.getTokensAndNFTs(walletAddress).then((res) => {
            return res?.data?.result || {};
        });
        const { nfts = [], tokens = [], native } = tokensAndNFTs || {};
        const findData = getChainInfo()['Solana'];
        const { icon: defaultIcon, symbol: defaultSymbol } = findData || {};
        // tokens 前面添加 native
        tokens.unshift({
            address: 'native',
            amount: balance,
            image: defaultIcon,
            name: defaultSymbol,
            symbol: defaultSymbol,
        });
        const exchangeRateData = await this.getPrice([...new Set([...tokenAddressList, ...tokens.map((item) => item.address)])]);
        // format data
        tokens.forEach((token) => {
            const price = (exchangeRateData.find((item) => item.address?.toLowerCase() == token.address?.toLowerCase()) || {})?.price || 0;
            if (!token.image && token.address !== 'native') {
                token.image = tokenData.tokens.find((item) => item.address?.toLowerCase() == token.address?.toLowerCase())?.logoURI || '';
            }
            token.image = token.image || defaultTokenIcon;
            // Number((token.amount || 0).toString()) / Math.pow(10, token.decimals || 9)
            token.amount = new BigNumber(token.amount || 0)
                .dividedBy(Math.pow(10, token.decimals ?? 9))
                .decimalPlaces(10, 1)
                .toNumber();
            token.unitPrice = price;
            token.price = token.amount * price;
            token['24hChange'] = (exchangeRateData.find((item) => item.address?.toLowerCase() == token.address?.toLowerCase()) || {})['24hChange'] || '';
            if (token['24hChange']) {
                token['24hChange'] = Number(toNonExponential(token['24hChange'], 2));
            }
            else {
                token['24hChange'] = 0;
            }
        });
        // format data nft add tokeId
        // getNftMetadata
        for (let i = 0; i < nfts.length; i++) {
            const nft = nfts[i];
            const uriData = nft?.metadata?.data?.uriData || {};
            const name = nft.name || uriData.name;
            const animationUrl = nft.animation_url || uriData.animation_url;
            const image = nft.image || uriData.image;
            const externalUrl = nft?.metadata?.data?.uriData?.external_url || nft?.metadata?.data?.uriData?.external_link;
            const description = nft.description || uriData.description || nft?.metadata?.data?.uriData?.description || '';
            let sellerFeeBasisPoints = nft.sellerFeeBasisPoints || uriData.seller_fee_basis_points || nft.metadata?.data?.sellerFeeBasisPoints || 0;
            const tokenId = nft.mint;
            const address = nft.mint;
            const attributes = nft?.metadata?.data?.uriData?.attributes || [];
            if (sellerFeeBasisPoints) {
                sellerFeeBasisPoints = Number((sellerFeeBasisPoints / 10000) * 100).toFixed(2) + '%';
            }
            nfts[i] = { name, address, image, description, sellerFeeBasisPoints, tokenId, animationUrl, externalUrl, data: { attributes, description, sellerFeeBasisPoints, externalUrl } };
        }
        let totalPrice = tokens.reduce((total, item) => {
            return item.price + total;
        }, 0) || 0;
        nfts;
        await database.tokensAndNFTs.put({
            id,
            ...tokensAndNFTs,
            nfts,
            tokens,
            totalPrice,
            exchangeRateData,
            walletAddress,
            importTokenList,
            updateTime: dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss'),
        });
        return {
            chainName: this.chainName,
            chainId: this.chainId,
            allTokens: tokenData,
            nfts,
            tokens,
            totalPrice,
            walletAddress,
            exchangeRateData,
            importTokenList,
        };
    }
    // address
    async getAddress() {
        if (this.mockAddress) {
            return this.mockAddress;
        }
        if (this.provider?.getAddress) {
            return await this.provider?.getAddress();
        }
        return this.provider?.publicKey?.toBase58?.();
    }
    async getPrice(addressList, useCache = true, chainId = this.chainId) {
        const address = await this.getAddress();
        const id = md5(`${this.chainName}_${chainId}_${address}_${addressList.join('_')}_${this.currency}`);
        addressList = addressList.map((address) => {
            if (address.toLowerCase() == WSOL_TOKEN_ADDRESS) {
                return 'native';
            }
            else {
                return address;
            }
        });
        let { list: exchangeRateList } = await this.request({
            id,
            useCache,
            throttleWait: 1000 * 10,
            fn: () => {
                return this.rpc.getPrice(addressList, [this.currency]).then((res) => {
                    return res.data.result;
                });
            },
            dataBaseModel: database.exchangeRate,
        });
        exchangeRateList = exchangeRateList.map((item) => {
            const currencie = item.currencies.find((o) => o.type.toLowerCase() == this.currency.toLowerCase()) || {};
            return { address: item.address, price: currencie.price, ['24hChange']: currencie['24hChange'], currency: this.currency.toLowerCase() };
        });
        return exchangeRateList;
    }
    // getAllToken
    async getAllToken() {
        let data = await this.request({
            primaryKey: {
                chainType: this.chainType,
                chainId: this.chainId,
                chainName: this.chainName,
            },
            throttleWait: 1000 * 60,
            fn: () => {
                return this.commonApi.getAllToken(this.chainName.toLowerCase());
            },
            dataBaseModel: database.allTokenInfo,
        });
        return data;
    }
    async cancelTransaction(params) {
        return new Promise((resolve, reject) => { });
    }
    async speedupTransaction(params) {
        return new Promise((resolve, reject) => { });
    }
    // serializeTransaction
    async serializeTransaction(type, data) {
        return this.rpc.serializeTransaction(type, data).then((res) => {
            return res.transaction.serialized;
        });
    }
    // Withdraw
    async withdraw() {
        const address = await this.getAddress();
        let transaction = await this.serializeTransaction('unwrap-sol', { address });
        transaction = Transaction.from(bs58.decode(transaction));
        return this.provider.signAndSendTransaction(transaction);
    }
    async sendTransaction(mintAddress, to = '', amount = '0') {
        if (mintAddress?.toLowerCase?.() == 'native') {
            const address = await this.getAddress();
            const decimals = await this.getDecimalsByAddress(mintAddress);
            const lamports = numbro(amount)
                .multiply(10 ** decimals)
                .format({ trimMantissa: true, mantissa: 0 });
            const unsignedTransaction = await this.serializeTransaction('transfer-sol', { receiver: to, sender: address, lamports: Number(lamports), payer: address });
            const transaction = Transaction.from(bs58.decode(unsignedTransaction));
            return this.provider.signAndSendTransaction(transaction);
        }
        else {
            return this.sendTokenTransaction(mintAddress, to, amount);
        }
    }
    async sendTokenTransaction(mintAddress, to, amount) {
        const decimals = await this.getDecimalsByAddress(mintAddress);
        const address = await this.getAddress();
        const unsignedTransaction = await this.serializeTransaction('transfer-token', {
            receiver: to,
            amount: Math.floor(Number(amount) * 10 ** decimals),
            sender: address,
            mint: mintAddress,
            payer: address,
        });
        const transaction = Transaction.from(bs58.decode(unsignedTransaction));
        return this.provider.signAndSendTransaction(transaction);
    }
    async sendNftTransaction(mintAddress, toAddress, tokenId, amount = 1) {
        return this.sendTokenTransaction(mintAddress, toAddress, amount.toString());
    }
    // getTransactionsListByMint
    async getTransactionsListByMint(toenAddress = 'native', useCache = true) {
        const account = await this.getAddress();
        const id = md5(`${this.chainName}_${this.chainId}_${account}_${toenAddress}`.toLowerCase());
        return this.request({
            id,
            useCache,
            fn: async () => {
                const tansactionInfo = await database.tansactionInfoList.where({ id }).first();
                let { list = [] } = tansactionInfo || {};
                const firstSignature = list[0]?.signature;
                let direction = 'until';
                return this.rpc.getEnhancedGetTokenTransactionsByAddress(account, firstSignature, toenAddress, direction).then((res) => {
                    let listData = [];
                    if (firstSignature) {
                        listData = [...(res || []), ...list];
                    }
                    else {
                        listData = res || [];
                    }
                    // sort by timestamp
                    listData = listData
                        .reduce((total, item) => {
                        const index = total.findIndex((o) => o.signature == item.signature);
                        if (index == -1) {
                            total.push(item);
                        }
                        return total;
                    }, [])
                        .sort((a, b) => {
                        return b.blockTime - a.blockTime;
                    })
                        .map((item) => {
                        item.direction = direction;
                        return item;
                    });
                    return listData;
                });
            },
            dataBaseModel: database.tansactionInfoList,
        }).then(({ list }) => {
            list = list.map((item) => {
                item = {
                    ...item,
                    chainId: this.chainId,
                    hash: item.signature,
                    shortHash: item.signature.substring(0, 5) + '...' + item.signature.substring(item.signature.length - 5, item.signature.length),
                    from: item.data.sender,
                    to: item.data.receiver,
                    value: item.lamportsChange / 10 ** 9,
                    date: dayjs(item.blockTime * 1000).format(DateFormat),
                };
                if (item.status == 'failed') {
                    item.action = 'failed';
                }
                else if (item.data.sender?.toLowerCase?.() === account?.toLowerCase?.()) {
                    item.action = 'out';
                }
                else if (item.data.sender?.toLowerCase?.() !== account?.toLowerCase?.() && item.type !== 'unknown') {
                    item.action = 'in';
                }
                item.data.lamportsTransfered = Math.abs(item.data.lamportsTransfered);
                if (item.type === 'transfer-sol') {
                    item.value = item.data.lamportsTransfered / 10 ** 9;
                    item.symbol = 'SOL';
                }
                else if (item.type === 'transfer-token') {
                    // item.value = item.data.amountTransfered / 10 ** 9;
                    item.value = -1;
                    item.symbol = 'Unknown';
                }
                else if (item.type === 'unknown') {
                    item.value = -1;
                    item.symbol = 'Unknown';
                }
                return item;
            });
            // if (address && address !== 'native') {
            //   list = list.filter((item: any) => item.to === address || item.from === address || item.data.mint == address);
            // }
            return list;
        });
    }
    async getTransactionsList(tokenAddress = 'native', useCache = true) {
        const account = await this.getAddress();
        const id = md5(`${this.chainName}_${this.chainId}_${account}_${tokenAddress}`.toLowerCase());
        if (tokenAddress != 'native') {
            return this.getTransactionsListByMint(tokenAddress, useCache);
        }
        return this.request({
            id,
            useCache,
            fn: async () => {
                const tansactionInfo = await database.tansactionInfoList.where({ id }).first();
                const { list = [] } = tansactionInfo || {};
                const firstSignature = list[0]?.signature;
                let direction = list[0]?.direction || 'before';
                direction = 'until';
                return this.rpc.getTransactionsList(account, firstSignature, direction).then((res) => {
                    let listData = [];
                    if (firstSignature) {
                        listData = [...(res.data.result || []), ...list];
                    }
                    else {
                        listData = res.data.result || [];
                    }
                    // sort by timestamp
                    listData = listData
                        .reduce((total, item) => {
                        const index = total.findIndex((o) => o.signature == item.signature);
                        if (index == -1) {
                            total.push(item);
                        }
                        return total;
                    }, [])
                        .sort((a, b) => {
                        return b.blockTime - a.blockTime;
                    })
                        .map((item) => {
                        item.direction = direction;
                        return item;
                    });
                    return listData;
                });
            },
            dataBaseModel: database.tansactionInfoList,
        }).then(({ list }) => {
            list = list.map((item) => {
                item = {
                    ...item,
                    chainId: this.chainId,
                    hash: item.signature,
                    shortHash: item.signature.substring(0, 5) + '...' + item.signature.substring(item.signature.length - 5, item.signature.length),
                    from: item.data.sender,
                    to: item.data.receiver,
                    value: formatAmount(new BigNumber(item.lamportsChange ?? '0')
                        .dividedBy(Math.pow(10, item.data.decimals ?? 9))
                        .decimalPlaces(8, 1)
                        .toNumber(), 8),
                    date: dayjs(item.blockTime * 1000).format(DateFormat),
                };
                if (item.status == 'failed') {
                    item.action = 'failed';
                }
                else if (item.data.sender?.toLowerCase?.() === account?.toLowerCase?.()) {
                    item.action = 'out';
                }
                else if (item.data.sender?.toLowerCase?.() !== account?.toLowerCase?.() && item.type !== 'unknown') {
                    item.action = 'in';
                }
                item.data.lamportsTransfered = Math.abs(item.data.lamportsTransfered);
                if (item.type === 'transfer-sol') {
                    item.value = formatAmount(item.data.lamportsTransfered / 10 ** (item.data.decimals ?? 9), 8);
                    item.symbol = 'SOL';
                }
                else if (item.type === 'transfer-token') {
                    item.value = formatAmount((item.data.lamportsTransfered || 1) / 10 ** (item.data.decimals ?? 8), 8);
                    item.symbol = 'Unknown';
                }
                else if (item.type === 'unknown') {
                    item.value = -1;
                    item.symbol = 'Unknown';
                }
                return item;
            });
            if (tokenAddress && tokenAddress !== 'native') {
                list = list.filter((item) => item.to === tokenAddress || item.from === tokenAddress || item.data.mint == tokenAddress);
            }
            return list;
        });
    }
    async getTokensByTokenAddresses(tokenAddressList) {
        let res = await this.rpc.getTokensByTokenAddresses(await this.getAddress(), tokenAddressList);
        let tokenData = await this.getAllToken();
        if (res && res.length) {
            res.map((item) => {
                let findData = tokenData.tokens.find((token) => token.address?.toLowerCase?.() === item.address?.toLowerCase?.()) || {};
                item.image = findData.logoURI || findData.image || item.logoURI || item.image || defaultTokenIcon;
                return item;
            });
        }
        return res;
    }
    // getEstimateGas
    async getEstimateGas(params) {
        return new Promise((resolve, reject) => { });
    }
    // getSuggestedGasFees
    async getSuggestedGasFees() {
        return new Promise((resolve, reject) => { });
    }
    validateAddress(address) {
        try {
            let pubkey = new PublicKey(address);
            let isSolana = PublicKey.isOnCurve(pubkey.toBuffer());
            return !!isSolana;
        }
        catch (error) {
            throw new Error(this.t('please_enter_correct_address'));
        }
    }
    async addUserAddress(params) {
        let walletAddress = await this.getAddress();
        let userAddressData = await database.userAddressList.where({ userAddress: params.userAddress, walletAddress }).first();
        const shortUserAddress = params.userAddress.substring(0, 5) + '...' + params.userAddress.substring(params.userAddress.length - 5, params.userAddress.length);
        if (userAddressData || params.id) {
            return await database.userAddressList.put({ ...(userAddressData || {}), ...params, shortUserAddress, chainType: this.chainType, walletAddress });
        }
        else {
            const id = uuidv4();
            const { createIcon } = await import('@download/blockies');
            const imgSrc = createIcon({
                seed: params.userAddress,
                size: 10,
                scale: 10,
            }).toDataURL('image/png');
            return await database.userAddressList.put({ ...params, imgSrc, shortUserAddress, chainType: this.chainType, id, walletAddress });
        }
    }
    async getUserAddress(params) {
        let walletAddress = await this.getAddress();
        return await database.userAddressList.where({ chainType: this.chainType, walletAddress }).toArray();
    }
    //deleteUserAddress
    async deleteUserAddress(id) {
        let addressInfo = await database.userAddressList.where({ id }).first();
        if (!addressInfo) {
            throw new Error(this.t('address_not_found'));
        }
        return await database.userAddressList.delete(id);
    }
    // addCustomToken
    async addCustomToken(params) {
        const id = md5(`${this.chainName}_${params.address}_${this.chainId}`);
        await database.customTokenList.put({
            id,
            ...params,
            chainName: this.chainName,
            chainId: this.chainId,
        });
        return database.customTokenList.where({ chainName: this.chainName, address: params.address }).toArray();
    }
    async updateCustomTokenByAddress(address, type) {
        const id = md5(`${this.chainName}_${address}_${this.chainId}`);
        let findData = await database.customTokenList.where({ id }).toArray();
        if (findData && findData.length) {
            if (type == 'delete') {
                findData[0].hidden = true;
            }
            else {
                findData[0].hidden = false;
            }
            await database.customTokenList.put(findData[0]);
            return true;
        }
        else {
            return true;
        }
    }
    async addCustomTokenList(params) {
        if (!params.address) {
            throw new Error(this.t('invalid_address'));
        }
        const id = md5(`${this.chainName}_${params.address}`);
        await database.customTokenList.put({
            ...params,
            chainName: this.chainName,
            id,
        });
        return true;
    }
    async getCustomTokenList() {
        return (await database.customTokenList.where({ chainId: this.chainId }).toArray()).filter((item) => item.hidden !== true);
    }
    async addImportTokenData(params) {
        const updateTime = dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss');
        params.chainName = params.chainName?.toLowerCase();
        params.chainNetwork = params.chainNetwork?.toLowerCase();
        if (params.chainId) {
            params.chainId = Number(params.chainId);
        }
        if (params.userAddress) {
            params.userAddress = params.userAddress?.toLowerCase();
        }
        await database.importTokenList.put({
            ...params,
            updateTime,
        });
        return true;
    }
    getCurrentChainInfo() {
        return window.__webWalletData.chainInfo ?? {};
    }
    async getImpoertTokenList(params) {
        params = {
            chainName: this.chainName.toLowerCase(),
            chainId: Number(this.chainId),
            ...params,
        };
        params.chainNetwork = params.chainNetwork?.toLowerCase();
        return await database.importTokenList.where(params).toArray();
    }
    async getDecimalsByAddress(address, chainNetwork) {
        let tokenData = await this.getAllToken();
        const cacheKey = await this.getTokenAndNftCacheKey();
        const tokenAndNFTs = await database.tokensAndNFTs.where({ id: cacheKey }).first();
        let decimals = 0;
        if (address.toLowerCase() === 'native') {
            decimals = 9;
        }
        else {
            decimals = [...(tokenAndNFTs?.tokens || []), ...tokenData.tokens].find((item) => item.address?.toLowerCase?.() == address?.toLowerCase?.())?.decimals || 0;
        }
        try {
            const userAddress = (await this.getAddress()).toLowerCase();
            if (!decimals) {
                let importTokenData = await this.getImpoertTokenList({ userAddress, chainNetwork });
                decimals = importTokenData.find((item) => item.tokenAddress?.toLowerCase?.() == address?.toLowerCase?.())?.metaData?.decimals || 0;
            }
            if (!decimals) {
                const totkenList = getCacheTokenList(userAddress, this.chainName, Number(this.chainId));
                decimals = Number(totkenList.find((item) => item.address?.toLowerCase?.() == address?.toLowerCase?.())?.decimals || 0);
            }
        }
        catch (error) {
            console.log('getDecimalsByAddress', error);
        }
        if (!decimals && address?.toLowerCase?.() === 'native') {
            decimals = decimals ?? 9;
        }
        return decimals;
    }
    // removeImportTokenData
    async removeImportTokenData(data) {
        let params = {
            chainName: data.chainName,
            chainId: Number(data.chainId),
            tokenAddress: data.tokenAddress,
            chainNetwork: data.chainNetwork,
            userAddress: data.userAddress.toLowerCase(),
        };
        return await database.importTokenList.where(params).delete();
    }
    // getQuote
    async getQuote(params) {
        const { inputMint, outputMint, isTry, amount } = params;
        let inputMintDecimals = (await this.getDecimalsByAddress(inputMint)) ?? 9;
        let outputMintDecimals = (await this.getDecimalsByAddress(outputMint)) ?? 9;
        let selfAddress = await this.getAddress();
        let quoteRes = [];
        if (params.outputMint) {
            // safe transfer
            let _amount = new BigNumber(params.amount).multipliedBy(Math.pow(10, inputMintDecimals)).integerValue(1).toString(10);
            if (Number(_amount) <= 0) {
                throw new Error(this.t('invalid_amount'));
            }
            const { inputMint, outputMint, slippage } = params;
            quoteRes = await this.rpc.getQuote(selfAddress, { amount: _amount, inputMint, outputMint, slippage });
        }
        if (!quoteRes || quoteRes.length == 0) {
            quoteRes = [
                {
                    fees: {
                        totalFeeAndDeposits: 0,
                    },
                },
            ];
        }
        let routeData = quoteRes[0] || {};
        try {
            const address = await this.getAddress();
            const id = await this.getTokenAndNftCacheKey();
            const tokensAndNFTs = (await database.tokensAndNFTs.where({ id }).first()) || {};
            if (!tokensAndNFTs.tokens) {
                console.log('getQuote', `${this.chainName}_${this.chainId}_${address}`);
            }
            let tokenBalance = tokensAndNFTs.tokens?.find((item) => item.address?.toLowerCase?.() == inputMint?.toLowerCase?.())?.amount || 0;
            if (inputMint?.toLowerCase?.() == WSOL_TOKEN_ADDRESS?.toLowerCase?.()) {
                tokenBalance = Number(tokensAndNFTs.native);
            }
            routeData.maxAmount = (tokenBalance - (routeData?.fees?.totalFeeAndDeposits || 0)) / 10 ** inputMintDecimals;
            routeData.maxAmount = routeData.maxAmount > 0 ? routeData.maxAmount : 0;
            let outAmount = routeData.outAmount;
            if (outAmount) {
                routeData.outAmountValue = outAmount / 10 ** outputMintDecimals;
                routeData.outAmountValue = numbro(routeData.outAmountValue > 0 ? routeData.outAmountValue : 0).format({
                    thousandSeparated: false,
                    trimMantissa: true,
                    mantissa: outputMintDecimals,
                });
            }
        }
        catch (error) {
            console.log('getQuote', error);
        }
        // get symbol by address
        let inputSymbol = await this.getSymbolByAddress(inputMint?.toLowerCase?.() == WSOL_TOKEN_ADDRESS?.toLowerCase?.() ? 'native' : inputMint);
        routeData.inputSymbol = inputSymbol;
        let outSymbol = outputMint ? await this.getSymbolByAddress(outputMint?.toLowerCase?.() == WSOL_TOKEN_ADDRESS?.toLowerCase?.() ? 'native' : outputMint) : '';
        routeData.outSymbol = outSymbol;
        // get token icon by address
        let inputIcon = await this.getTokenIconByAddress(inputMint);
        routeData.inputIcon = inputIcon;
        if (outputMint) {
            let outIcon = await this.getTokenIconByAddress(outputMint);
            routeData.outIcon = outIcon;
        }
        if (Number(routeData.outAmount) && Number(routeData.inAmount)) {
            routeData.rateValue = numbro((1 / (Number(routeData.inAmount) / 10 ** inputMintDecimals)) * (routeData.outAmount / 10 ** outputMintDecimals)).format({
                trimMantissa: true,
                mantissa: 8,
            });
        }
        else {
            routeData.rateValue = '';
        }
        if (routeData.fees?.totalFeeAndDeposits) {
            routeData.networkFees = numbro(routeData.fees.totalFeeAndDeposits)
                .divide(LAMPORTS_PER_SOL)
                // .divide(10 ** inputMintDecimals)
                .format({
                trimMantissa: true,
                mantissa: 8,
            });
        }
        else {
            routeData.networkFees = '';
        }
        // routeData.marketInfos = routeData?.marketInfos || [{}];
        routeData.inputMintDecimals = inputMintDecimals;
        routeData.outputMintDecimals = outputMintDecimals;
        routeData.youPayAmount = isTry ? 0 : amount;
        // if (routeData.fees?.minimumSOLForTransaction) {
        //   routeData.fees.minimumSOLForTransaction = routeData.fees?.minimumSOLForTransaction / LAMPORTS_PER_SOL;
        // }
        console.log(routeData);
        return routeData;
    }
    async getSymbolByAddress(address) {
        let allTokenInfo = await database.allTokenInfo.where({ chainName: this.chainName, chainId: this.chainId, chainType: this.chainType }).first();
        let tokenInfo = allTokenInfo?.tokens?.find?.((item) => item.address?.toLowerCase?.() == address?.toLowerCase?.());
        if (tokenInfo) {
            return tokenInfo.symbol;
        }
        const id = await this.getTokenAndNftCacheKey();
        const tokensAndNFTs = await database.tokensAndNFTs.where({ id }).first();
        tokenInfo = tokensAndNFTs?.tokens?.find?.((item) => item.address?.toLowerCase?.() == address?.toLowerCase?.());
        if (tokenInfo) {
            return tokenInfo.symbol;
        }
        const userAddress = (await this.getAddress()).toLowerCase();
        let importTokenData = await this.getImpoertTokenList({ userAddress, chainNetwork: this.getCurrentChainInfo().chainNetwork });
        tokenInfo = importTokenData.find((item) => item.tokenAddress?.toLowerCase?.() == address?.toLowerCase?.());
        if (tokenInfo) {
            return tokenInfo.symbol;
        }
        return 'SOL';
    }
    // get token icon by address
    async getTokenIconByAddress(address, defailtIcon = true) {
        let allTokenInfo = await database.allTokenInfo.where({ chainName: this.chainName, chainId: this.chainId, chainType: this.chainType }).first();
        let tokenInfo = allTokenInfo?.tokens?.find?.((item) => item.address?.toLowerCase?.() == address?.toLowerCase?.());
        if (tokenInfo?.symbol == 'wSOL') {
            let sIcon = solanaLightIcon;
            const themeMode = typeof window === 'undefined' ? '' : (location.href.match(/theme=(\w+)/) || [])[1] || window.storage.getItem('themeMode') || '';
            if (!themeMode || themeMode == 'dark') {
                sIcon = solanaIcon;
            }
            return sIcon;
        }
        if (tokenInfo && !!tokenInfo.logoURI) {
            return tokenInfo.logoURI;
        }
        const id = await this.getTokenAndNftCacheKey();
        const tokensAndNFTs = await database.tokensAndNFTs.where({ id }).first();
        tokenInfo = tokensAndNFTs?.tokens?.find((item) => item.address?.toLowerCase?.() == address?.toLowerCase?.());
        if (tokenInfo) {
            return tokenInfo.image;
        }
        const userAddress = (await this.getAddress()).toLowerCase();
        let importTokenData = await this.getImpoertTokenList({ userAddress, chainNetwork: this.getCurrentChainInfo().chainNetwork });
        tokenInfo = importTokenData.find((item) => item.tokenAddress?.toLowerCase?.() == address?.toLowerCase?.());
        if (tokenInfo) {
            return tokenInfo.image || defaultTokenIcon;
        }
        return defaultTokenIcon;
    }
    // getSwapTransactions
    async getSwapTransactions(routeData) {
        let selfAddress = await this.getAddress();
        return this.rpc.getSwapTransactions(selfAddress, routeData);
    }
    async tokenSwap(params) {
        const { inputMint } = params;
        // get quote
        let routeData = await this.getQuote(params);
        // Validate the data
        let minimumSOLForTransaction = routeData.fees.minimumSOLForTransaction;
        const totalFeeAndDeposits = routeData.fees.totalFeeAndDeposits;
        let balance = await this.getBalance();
        if (inputMint?.toLowerCase?.() === WSOL_TOKEN_ADDRESS?.toLowerCase?.()) {
            if (Number(params.amount) * LAMPORTS_PER_SOL > balance - totalFeeAndDeposits - minimumSOLForTransaction) {
                // ((balance - totalFeeAndDeposits - minimumSOLForTransaction) / LAMPORTS_PER_SOL)
                params.amount = new BigNumber(balance - totalFeeAndDeposits - minimumSOLForTransaction).toString(10);
                routeData = await this.getQuote(params);
                minimumSOLForTransaction = routeData.fees.minimumSOLForTransaction;
            }
        }
        else if (balance < minimumSOLForTransaction) {
            // 抛出 solana 账户余额不足
            throw new Error(this.t('network_fee_is_not_enough'));
        }
        let transactions = await this.getSwapTransactions(routeData);
        transactions = transactions.map((item) => Transaction.from(bs58.decode(item.serialized)));
        const signTransaction = await this.provider.signAllTransactions(transactions);
        console.log('signTransaction', signTransaction.length);
        const connection = this.getConnection();
        for (let i = 0; i < signTransaction.length; i++) {
            console.log('for', i);
            const signedTransaction = signTransaction[i];
            let tryCount = 1;
            let txId = null;
            do {
                try {
                    console.log('for', i, 'try', tryCount);
                    txId = await connection.sendRawTransaction(signedTransaction?.serialize(), {
                        preflightCommitment: 'recent',
                    });
                }
                catch (error) {
                    console.log('tokenSwap', error);
                }
                tryCount++;
                await new Promise((resolve) => setTimeout(resolve, 3000));
            } while (!txId && tryCount <= 3);
            if (!txId) {
                throw new Error(this.t('transaction_failed'));
            }
            signedTransaction.txId = txId;
            console.log('for', i, signedTransaction.txId);
            // TODO getTransaction https://minijoy.feishu.cn/docx/doxcnE2eIXKOyUYf5dVEsQRvfNg
            // pass
        }
        return signTransaction.map((item) => {
            return {
                txId: item.txId,
            };
        });
    }
    // getNetworkFees
    async getNetworkFees() {
        return { value: SolanaNetworkFee };
    }
    async getNftNetworkFees() {
        return { value: SolanaNetworkFee };
    }
    getChainById(id) {
        const chains = getChainInfo();
        const chainData = chains.Solana.chainData;
        const network = chainData[Number(id)].network;
        const fullname = chainData[Number(id)].fullname;
        if (!network) {
            return undefined;
        }
        return {
            id: Number(id),
            name: 'Solana',
            network: network,
            symbol: 'SOL',
            icon: chains['Solana'].icon,
            chainType: 'solana',
            fullname,
        };
    }
    async nftBatchSend(toAddress, nfts) {
        return new Promise((resolve, reject) => { });
    }
    async tokenBatchSend(toAddress, tokens) {
        return new Promise((resolve, reject) => { });
    }
    async swapCreateOrder(params) {
        return new Promise((resolve, reject) => {
            resolve({
                quoteMessage: this.t('not_support_fusion_swap'),
            });
        });
    }
    async swapSubmitOrder(assress, params) {
        return new Promise((resolve, reject) => { });
    }
    async tokenSwapFusionMode(params) {
        return new Promise((resolve, reject) => { });
    }
    getBridgeSupportedTokens(chainId) {
        return {};
    }
    async getBridgeQuote(params) {
        return {};
    }
    async bridgeApproveToken(params, walletAccount) { }
    async tokenBridge(params) {
        return '';
    }
    async getNftByAddress(address, id) {
        return {};
    }
    async importNftData(chainInfo, nftInfo) {
        return {};
    }
    async getBridgeHistoryList(status) {
        return new Promise((resolve, reject) => {
            resolve([]);
        });
    }
    async bridgeStatus(transactionHash) {
        return new Promise((resolve, reject) => {
            resolve('');
        });
    }
    /**
     * 根据btc地址获取evm地址
     * @param btcAddress
     * @returns
     */
    async getEvmAddressByBtcAddress(btcAddress) {
        return '';
    }
    /**
     * 根据evm地址获取btc地址
     * @param evmAddress
     */
    async getBtcAddressByEvmAddress(evmAddress) {
        return '';
    }
}
//# sourceMappingURL=solana-service.js.map